import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const RouterContext = createContext({
  path: "/",
  hash: "",
  params: {},
  setParams: () => {},
  navigate: () => {},
});

const matchPath = (pattern, pathname) => {
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = pathname.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return { matched: false, params: {} };

  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i];
    const segment = pathParts[i];
    if (part.startsWith(":")) {
      params[part.slice(1)] = decodeURIComponent(segment);
    } else if (part !== segment) {
      return { matched: false, params: {} };
    }
  }
  return { matched: true, params };
};

export const BrowserRouter = ({ children }) => {
  const [path, setPath] = useState(window.location.pathname || "/");
  const [hash, setHash] = useState(window.location.hash.replace("#", "") || "");
  const [params, setParams] = useState({});

  const navigate = useCallback(
    (to) => {
      const url = new URL(to, window.location.origin);
      const nextPath = url.pathname || "/";
      const nextHash = url.hash.replace("#", "") || "";
      if (nextPath === path && nextHash === hash) return;
      window.history.pushState({}, "", url.toString());
      setPath(nextPath);
      setHash(nextHash);
    },
    [path, hash]
  );

  useEffect(() => {
    const handlePop = () => {
      setPath(window.location.pathname || "/");
      setHash(window.location.hash.replace("#", "") || "");
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  return (
    <RouterContext.Provider value={{ path, hash, params, setParams, navigate }}>
      {children}
    </RouterContext.Provider>
  );
};

export const Routes = ({ children }) => {
  const { path, setParams } = useContext(RouterContext);
  const prevParamsRef = React.useRef(null);

  const match = React.useMemo(() => {
    let elementToRender = null;
    let matchedParams = {};

    React.Children.forEach(children, (child) => {
      if (!child || elementToRender) return;
      const { path: pattern, element } = child.props || {};
      if (!pattern) return;
      const { matched, params } = matchPath(pattern, path);
      if (matched) {
        matchedParams = params;
        elementToRender = element || null;
      }
    });

    return { elementToRender, matchedParams };
  }, [children, path]);

  useEffect(() => {
    const prev = prevParamsRef.current || {};
    const next = match.matchedParams || {};

    const sameKeys =
      Object.keys(prev).length === Object.keys(next).length &&
      Object.keys(prev).every((key) => prev[key] === next[key]);

    if (!sameKeys) {
      setParams(next);
      prevParamsRef.current = next;
    }
  }, [match.matchedParams, setParams]);

  return match.elementToRender;
};

export const Route = ({ element }) => element || null;

export const useNavigate = () => {
  const { navigate } = useContext(RouterContext);
  return navigate;
};

export const useLocation = () => {
  const { path, hash } = useContext(RouterContext);
  return { pathname: path, hash: hash ? `#${hash}` : "" };
};

export const useParams = () => {
  const { params } = useContext(RouterContext);
  return params;
};

export const Link = ({ to, children, ...rest }) => {
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
};
